import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../utils/axios";

export type CandidateConfidence = "HIGH" | "MEDIUM" | "LOW";
export type CandidateStatus = "PENDING_REVIEW" | "APPROVED" | "REJECTED";
export type CandidateGenerationStatus =
  | "NOT_STARTED"
  | "QUEUED"
  | "PROCESSING"
  | "DONE"
  | "FAILED";

export interface PropertyCandidate {
  id: number;
  roadId: number;
  externalParcelId: string;
  name: string;
  address: string;
  area: number;
  geom: any;
  distanceFromRoadM: number;
  confidence: CandidateConfidence;
  status: CandidateStatus;
  createdAt: string;
}

export interface PropertyCandidatesState {
  generationStatus: CandidateGenerationStatus;
  candidatesReviewed: boolean;
  bufferDistance: number;
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  candidates: PropertyCandidate[];
  status: "idle" | "loading" | "failed";
}

const initialState: PropertyCandidatesState = {
  generationStatus: "NOT_STARTED",
  candidatesReviewed: false,
  bufferDistance: 25,
  total: 0,
  pending: 0,
  approved: 0,
  rejected: 0,
  candidates: [],
  status: "idle",
};

export const fetchCandidates = createAsyncThunk(
  "propertyCandidates/fetch",
  async (associationId: number) => {
    const response = await axiosInstance.get(
      `/associations/${associationId}/property-candidates`
    );
    return response.data;
  }
);

export const approveCandidate = createAsyncThunk(
  "propertyCandidates/approve",
  async ({
    associationId,
    candidateId,
  }: {
    associationId: number;
    candidateId: number;
  }) => {
    await axiosInstance.patch(
      `/associations/${associationId}/property-candidates/${candidateId}/approve`
    );
    return candidateId;
  }
);

export const rejectCandidate = createAsyncThunk(
  "propertyCandidates/reject",
  async ({
    associationId,
    candidateId,
  }: {
    associationId: number;
    candidateId: number;
  }) => {
    await axiosInstance.patch(
      `/associations/${associationId}/property-candidates/${candidateId}/reject`
    );
    return candidateId;
  }
);

export const confirmCandidates = createAsyncThunk(
  "propertyCandidates/confirm",
  async (associationId: number) => {
    await axiosInstance.post(
      `/associations/${associationId}/property-candidates/confirm`
    );
  }
);

export const regenerateCandidates = createAsyncThunk(
  "propertyCandidates/regenerate",
  async ({
    associationId,
    bufferDistance,
  }: {
    associationId: number;
    bufferDistance: 25 | 50;
  }) => {
    await axiosInstance.post(
      `/associations/${associationId}/property-candidates/regenerate`,
      { bufferDistance }
    );
    return bufferDistance;
  }
);

const propertyCandidatesSlice = createSlice({
  name: "propertyCandidates",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCandidates.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchCandidates.fulfilled, (state, action) => {
        state.status = "idle";
        state.generationStatus = action.payload.generationStatus;
        state.candidatesReviewed = action.payload.candidatesReviewed;
        state.bufferDistance = action.payload.bufferDistance ?? 25;
        state.total = action.payload.total ?? 0;
        state.pending = action.payload.pending ?? 0;
        state.approved = action.payload.approved ?? 0;
        state.rejected = action.payload.rejected ?? 0;
        state.candidates = action.payload.candidates ?? [];
      })
      .addCase(fetchCandidates.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(approveCandidate.fulfilled, (state, action) => {
        const candidate = state.candidates.find((c) => c.id === action.payload);
        if (candidate) {
          candidate.status = "APPROVED";
          state.pending = Math.max(0, state.pending - 1);
          state.approved += 1;
        }
      })
      .addCase(rejectCandidate.fulfilled, (state, action) => {
        const candidate = state.candidates.find((c) => c.id === action.payload);
        if (candidate) {
          candidate.status = "REJECTED";
          state.pending = Math.max(0, state.pending - 1);
          state.rejected += 1;
        }
      })
      .addCase(confirmCandidates.fulfilled, (state) => {
        state.candidatesReviewed = true;
        // remaining PENDING_REVIEW become REJECTED
        state.candidates.forEach((c) => {
          if (c.status === "PENDING_REVIEW") c.status = "REJECTED";
        });
        state.pending = 0;
      })
      .addCase(regenerateCandidates.fulfilled, (state, action) => {
        state.generationStatus = "QUEUED";
        state.bufferDistance = action.payload;
        state.candidates = state.candidates.filter(
          (c) => c.status !== "PENDING_REVIEW"
        );
        state.pending = 0;
        state.total = state.candidates.length;
      });
  },
});

export default propertyCandidatesSlice.reducer;
